import React, { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { Paperclip, Download, Trash2, Upload, X, FileText, Image } from 'lucide-react';

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const CATEGORY_LABELS = {
  hyresavtal: 'Hyresavtal',
  kreditupplysning: 'Kreditupplysning',
  övrigt: 'Övrigt',
};

const CATEGORY_COLORS = {
  hyresavtal: 'bg-blue-100 text-blue-700',
  kreditupplysning: 'bg-purple-100 text-purple-700',
  övrigt: 'bg-gray-100 text-gray-700',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(fileType) {
  if (fileType?.startsWith('image/')) return Image;
  return FileText;
}

export default function AttachmentsSection({ applicationId, attachments = [], canUpload = false, onAttachmentsChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [category, setCategory] = useState('övrigt');
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Filtypen stöds inte. Tillåtna: PDF, DOCX, JPEG, PNG');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Filen är för stor. Max 10 MB.');
      return;
    }

    setUploading(true);
    try {
      const { currentUser } = await import('firebase/auth').then(m => m.getAuth());
      const storagePath = `application-attachments/${applicationId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      const attachment = {
        fileName: file.name,
        fileUrl,
        fileType: file.type,
        fileSize: file.size,
        storagePath,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser?.uid || '',
        uploadedByName: currentUser?.displayName || 'Okänd',
        category,
      };

      await updateDoc(doc(db, 'applications', applicationId), {
        attachments: arrayUnion(attachment),
      });

      if (onAttachmentsChange) {
        onAttachmentsChange([...attachments, attachment]);
      }
      setSuccess(`"${file.name}" har laddats upp.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Uppladdningen misslyckades. Försök igen.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = (attachment) => {
    window.open(attachment.fileUrl, '_blank');
  };

  return (
    <div className="border-t border-[#E2EAF0] pt-4 mt-4">
      <h3 className="text-base font-bold text-[#222222] mb-3 font-['Poppins'] flex items-center gap-2">
        <Paperclip size={18} />
        Bilagor
        {attachments.length > 0 && (
          <span className="text-xs font-normal text-gray-500">({attachments.length})</span>
        )}
      </h3>

      {attachments.length === 0 && !canUpload && (
        <p className="text-sm text-gray-500">Inga bilagor har bifogats.</p>
      )}

      {attachments.length > 0 && (
        <div className="space-y-2 mb-3">
          {attachments.map((att, idx) => {
            const Icon = getFileIcon(att.fileType);
            return (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Icon size={18} className="text-gray-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#222222] truncate">{att.fileName}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${CATEGORY_COLORS[att.category] || CATEGORY_COLORS.övrigt}`}>
                        {CATEGORY_LABELS[att.category] || att.category}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {formatFileSize(att.fileSize)}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(att.uploadedAt).toLocaleDateString('sv-SE')}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(att)}
                  className="p-2 text-[#62B8E9] hover:text-[#4a9fd4] hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                  title="Ladda ner"
                >
                  <Download size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {canUpload && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
            >
              <option value="hyresavtal">Hyresavtal</option>
              <option value="kreditupplysning">Kreditupplysning</option>
              <option value="övrigt">Övrigt</option>
            </select>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#62B8E9] hover:bg-[#5ba3d4] rounded-lg transition-colors disabled:opacity-50"
            >
              <Upload size={14} />
              {uploading ? 'Laddar upp...' : 'Ladda upp fil'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            onChange={handleUpload}
            className="hidden"
          />
          <p className="text-[10px] text-gray-400">PDF, DOCX, JPEG, PNG. Max 10 MB.</p>
          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}
          {success && (
            <p className="text-xs text-green-600">{success}</p>
          )}
        </div>
      )}
    </div>
  );
}
